import { EligibleLease, RenewalOffer } from '../../../models/Concession'
import { NextActionValue } from '../components/NextActionMark'
import { PipelineTab } from '../LeadsPipeline/pipelineTabs'

export type RenewalsBoardStage =
    | 'eligible'
    | 'draft'
    | 'offered'
    | 'transfer'
    | 'signing'
    | 'future_resident'
    | 'lost'

export type SigningSubstatus =
    | 'packet_not_sent'
    | 'send_failed'
    | 'waiting_on_resident'
    | 'awaiting_pm'

export const RENEWAL_BOARD_TABS: PipelineTab[] = [
    { id: 'eligible', label: 'Eligible', stage: 'leads', step_type: 'leads' },
    { id: 'draft', label: 'Draft', stage: 'leads', step_type: 'leads' },
    { id: 'offered', label: 'Offered', stage: 'leads', step_type: 'leads' },
    { id: 'transfer', label: 'Transfer', stage: 'leads', step_type: 'leads' },
    { id: 'signing', label: 'Signing', stage: 'leases', step_type: 'lease' },
    {
        id: 'future_resident',
        label: 'Future Resident',
        stage: 'future_resident',
        step_type: 'future_resident',
    },
    { id: 'lost', label: 'Lost', stage: 'leads', step_type: 'leads' },
]

export const SIGNING_SUBSTATUS_FILTERS: {
    value: SigningSubstatus | 'all'
    label: string
}[] = [
    { value: 'all', label: 'All' },
    { value: 'packet_not_sent', label: 'Packet not sent' },
    { value: 'send_failed', label: 'Send failed' },
    { value: 'waiting_on_resident', label: 'Waiting on resident' },
    { value: 'awaiting_pm', label: 'Awaiting PM' },
]

export const LOST_STATUS_FILTERS: Array<
    'all' | 'declined' | 'expired' | 'revoked'
> = ['all', 'declined', 'expired', 'revoked']

export function offerBoardStage(offer: RenewalOffer): RenewalsBoardStage {
    if (offer.status === 'accepted') {
        if (
            offer.lead_pipeline_stage === 'future_resident' ||
            offer.lease_package_status === 'signed'
        ) {
            return 'future_resident'
        }
        return 'signing'
    }
    if (offer.status === 'draft') return 'draft'
    if (offer.status === 'offered') return 'offered'
    if (offer.status === 'transfer_requested') return 'transfer'
    return 'lost'
}

export function signingSubstatus(offer: RenewalOffer): SigningSubstatus {
    if (
        offer.lease_package_status === 'awaiting_property' ||
        offer.lease_awaiting_property
    ) {
        return 'awaiting_pm'
    }
    if (offer.lease_package_status === 'sent') {
        return 'waiting_on_resident'
    }
    if (offer.packet_send_error) {
        return 'send_failed'
    }
    return 'packet_not_sent'
}

export function resolveRenewalNextAction(
    offer: RenewalOffer,
    stage: RenewalsBoardStage,
): NextActionValue | null {
    if (stage === 'eligible' || stage === 'draft') {
        return { waiting_on: 'us', label: 'Send offer' }
    }
    if (stage === 'offered') {
        return { waiting_on: 'them', label: 'Review offer' }
    }
    if (stage === 'transfer') {
        return { waiting_on: 'us', label: 'Assign a room' }
    }
    if (stage === 'signing') {
        const sub = signingSubstatus(offer)
        if (sub === 'packet_not_sent' || sub === 'send_failed') {
            return { waiting_on: 'us', label: 'Send lease packet' }
        }
        if (sub === 'awaiting_pm') {
            return { waiting_on: 'us', label: 'Counter-sign' }
        }
        return { waiting_on: 'them', label: 'Sign lease' }
    }
    return null
}

export function resolveEligibleNextAction(): NextActionValue {
    return { waiting_on: 'us', label: 'Send offer' }
}

export function signingSubstatusLabel(value: SigningSubstatus): string {
    switch (value) {
        case 'awaiting_pm':
            return 'Awaiting PM'
        case 'waiting_on_resident':
            return 'Waiting on resident'
        case 'send_failed':
            return 'Send failed'
        default:
            return 'Packet not sent'
    }
}

export function matchesQuery(
    query: string,
    name: string,
    email: string,
    ...extras: Array<string | null | undefined>
): boolean {
    const q = query.trim().toLowerCase()
    if (!q) return true
    const haystack = [name, email, ...extras]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
    return haystack.includes(q)
}

export function offerMatchesQuery(offer: RenewalOffer, query: string): boolean {
    return matchesQuery(
        query,
        offer.tenant_name,
        offer.tenant_email,
        offer.unit_name,
        offer.area_config_name,
        offer.area_label,
    )
}

export function eligibleMatchesQuery(
    lease: EligibleLease,
    query: string,
): boolean {
    return matchesQuery(
        query,
        lease.tenant_name,
        lease.tenant_email,
        lease.unit_name,
        lease.area_config_name,
        lease.area_label,
    )
}
